import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Loader2, Search, XIcon } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useLocationSuggestionsSearch } from '@/hooks/useLocationSuggestionsSearch';
const RECENT_SEARCHES_STORAGE_KEY = 'casalabia-header-search-recent-locations';
const RECENT_SEARCHES_LIMIT = 5;
const POPULAR_LOCATIONS = [
    {
        id: 'popular:como',
        label: 'Como, Como, Italy',
        locationType: 'CITY',
        shortLabel: 'Como',
        municipality: 'Como',
        city: 'Como',
        province: 'Como',
        provinceCode: 'CO',
        region: 'Lombardy',
        countryCode: 'IT',
        addressPatch: {
            municipality: 'Como',
            city: 'Como',
            province: 'Como',
            provinceCode: 'CO',
            region: 'Lombardy',
            countryCode: 'IT',
            formattedAddress: 'Como, Como, Italy',
            geo: { lat: 45.8080597, lng: 9.0851765 },
        },
        lat: 45.8080597,
        lng: 9.0851765,
    },
    {
        id: 'popular:lecco',
        label: 'Lecco, Lecco, Italy',
        locationType: 'CITY',
        shortLabel: 'Lecco',
        municipality: 'Lecco',
        city: 'Lecco',
        province: 'Lecco',
        provinceCode: 'LC',
        region: 'Lombardy',
        countryCode: 'IT',
        addressPatch: {
            municipality: 'Lecco',
            city: 'Lecco',
            province: 'Lecco',
            provinceCode: 'LC',
            region: 'Lombardy',
            countryCode: 'IT',
            formattedAddress: 'Lecco, Lecco, Italy',
            geo: { lat: 45.8565698, lng: 9.3976703 },
        },
        lat: 45.8565698,
        lng: 9.3976703,
    },
    {
        id: 'popular:milano',
        label: 'Milan, Milano, Italy',
        locationType: 'CITY',
        shortLabel: 'Milan',
        municipality: 'Milan',
        city: 'Milan',
        province: 'Milano',
        provinceCode: 'MI',
        region: 'Lombardy',
        countryCode: 'IT',
        addressPatch: {
            municipality: 'Milan',
            city: 'Milan',
            province: 'Milano',
            provinceCode: 'MI',
            region: 'Lombardy',
            countryCode: 'IT',
            formattedAddress: 'Milan, Milano, Italy',
            geo: { lat: 45.4642035, lng: 9.189982 },
        },
        lat: 45.4642035,
        lng: 9.189982,
    },
    {
        id: 'popular:roma',
        label: 'Rome, Roma, Italy',
        locationType: 'CITY',
        shortLabel: 'Rome',
        municipality: 'Rome',
        city: 'Rome',
        province: 'Roma',
        provinceCode: 'RM',
        region: 'Lazio',
        countryCode: 'IT',
        addressPatch: {
            municipality: 'Rome',
            city: 'Rome',
            province: 'Roma',
            provinceCode: 'RM',
            region: 'Lazio',
            countryCode: 'IT',
            formattedAddress: 'Rome, Roma, Italy',
            geo: { lat: 41.9027835, lng: 12.4963655 },
        },
        lat: 41.9027835,
        lng: 12.4963655,
    },
    {
        id: 'popular:firenze',
        label: 'Florence, Firenze, Italy',
        locationType: 'CITY',
        shortLabel: 'Florence',
        municipality: 'Florence',
        city: 'Florence',
        province: 'Firenze',
        provinceCode: 'FI',
        region: 'Tuscany',
        countryCode: 'IT',
        addressPatch: {
            municipality: 'Florence',
            city: 'Florence',
            province: 'Firenze',
            provinceCode: 'FI',
            region: 'Tuscany',
            countryCode: 'IT',
            formattedAddress: 'Florence, Firenze, Italy',
            geo: { lat: 43.7695604, lng: 11.2558136 },
        },
        lat: 43.7695604,
        lng: 11.2558136,
    },
];
function normalizeStoredLocation(item) {
    return {
        ...item,
        locationType: item.locationType ?? (item.type === 'PLACE' ? 'POINT' : item.type) ?? 'POINT',
    };
}
function safeReadRecentSearches() {
    if (typeof window === 'undefined') {
        return [];
    }
    try {
        const raw = window.localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.flatMap((item) => {
            if (!item || typeof item !== 'object') {
                return [];
            }
            const maybe = item;
            if (typeof maybe.id !== 'string'
                || typeof maybe.label !== 'string'
                || typeof maybe.lat !== 'number'
                || !Number.isFinite(maybe.lat)
                || typeof maybe.lng !== 'number'
                || !Number.isFinite(maybe.lng)) {
                return [];
            }
            return [normalizeStoredLocation(maybe)];
        }).slice(0, RECENT_SEARCHES_LIMIT);
    }
    catch {
        return [];
    }
}
function safeWriteRecentSearches(items) {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(items));
    }
    catch {
        // noop
    }
}
function getRecentSearchIdentity(location) {
    return `${location.locationType}:${location.label.trim().toLowerCase()}`;
}
function addRecentSearch(currentRecent, location) {
    const identity = getRecentSearchIdentity(location);
    const withoutCurrent = currentRecent.filter((item) => getRecentSearchIdentity(item) !== identity);
    return [location, ...withoutCurrent].slice(0, RECENT_SEARCHES_LIMIT);
}
export function SiteHeaderSearchDrawer({ open, onOpenChange, onSelectLocation, }) {
    const { t } = useTranslation();
    const { query, setQuery, loading, error, suggestions, activeIndex, setActiveIndex, status, reset, selectActiveSuggestion, moveNext, movePrev, } = useLocationSuggestionsSearch({ limit: 8, minLength: 3, debounceMs: 350 });
    const [recentSearches, setRecentSearches] = React.useState([]);
    React.useEffect(() => {
        if (!open) {
            return;
        }
        reset();
        setRecentSearches(safeReadRecentSearches());
    }, [open, reset]);
    const handlePickSuggestion = React.useCallback((suggestion) => {
        const nextRecent = addRecentSearch(safeReadRecentSearches(), suggestion);
        setRecentSearches(nextRecent);
        safeWriteRecentSearches(nextRecent);
        onSelectLocation(suggestion);
        onOpenChange(false);
    }, [onOpenChange, onSelectLocation]);
    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onOpenChange(false);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveNext();
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            movePrev();
            return;
        }
        if (event.key === 'Enter') {
            const activeSuggestion = selectActiveSuggestion();
            if (!activeSuggestion) {
                return;
            }
            event.preventDefault();
            handlePickSuggestion(activeSuggestion);
        }
    }, [handlePickSuggestion, moveNext, movePrev, onOpenChange, selectActiveSuggestion]);
    const fallbackLocations = recentSearches.length > 0 ? recentSearches : POPULAR_LOCATIONS;
    const fallbackTitle = recentSearches.length > 0
        ? t({
            en: 'Recent searches',
            de: 'Letzte Suchen',
            it: 'Ricerche recenti',
        })
        : t({
            en: 'Popular searches',
            de: 'Beliebte Suchen',
            it: 'Ricerche popolari',
        });
    return (_jsx(Drawer, { open: open, onOpenChange: onOpenChange, direction: "left", fixed: true, repositionInputs: false, disablePreventScroll: false, children: _jsx(DrawerContent, { overlayClassName: "z-[70]", className: cn('z-[71] h-full p-0 before:inset-0 before:rounded-none before:border-0', 'data-[vaul-drawer-direction=left]:w-screen data-[vaul-drawer-direction=left]:max-w-none', 'md:data-[vaul-drawer-direction=left]:w-full md:data-[vaul-drawer-direction=left]:max-w-[480px]'), children: _jsxs("div", { className: "flex h-full min-h-0 flex-col bg-background", children: [_jsx("div", { className: "flex items-center justify-end p-3", children: _jsx(Button, { variant: "ghost", size: "icon-lg", className: "touch-manipulation", onClick: () => onOpenChange(false), "aria-label": t({
                                en: 'Close search',
                                de: 'Suche schließen',
                                it: 'Chiudi ricerca',
                            }), children: _jsx(XIcon, { className: "size-6 stroke-1", "aria-hidden": true }) }) }), _jsx("div", { className: "px-5 pb-3", children: _jsx(DrawerTitle, { className: "text-lg font-medium", children: t({ en: 'Search', de: 'Suche', it: 'Cerca' }) }) }), _jsx("div", { className: "px-5 pb-2", children: _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" }), _jsx(Input, { value: query, onChange: (event) => setQuery(event.target.value), onKeyDown: handleKeyDown, placeholder: t({
                                        en: 'Search by city, district or postal code',
                                        de: 'Nach Stadt, Bezirk oder Postleitzahl suchen',
                                        it: 'Cerca per città, distretto o CAP',
                                    }), className: "h-11 pr-10 pl-9 text-base", "aria-label": t({
                                        en: 'Location search input',
                                        de: 'Standortsuche Eingabefeld',
                                        it: 'Campo di ricerca località',
                                    }) }), query.trim().length > 0 && (_jsx("button", { type: "button", className: "absolute right-1.5 top-1/2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground", onClick: () => setQuery(''), "aria-label": t({
                                        en: 'Clear search',
                                        de: 'Suche löschen',
                                        it: 'Cancella ricerca',
                                    }), children: _jsx(XIcon, { className: "size-4", "aria-hidden": true }) }))] }) }), _jsxs("div", { className: "min-h-0 flex-1 overflow-y-auto px-5 pb-[calc(env(safe-area-inset-bottom)+1rem)]", children: [status === 'loading' && (_jsxs("div", { className: "flex items-center gap-2 pt-3 text-sm text-muted-foreground", role: "status", "aria-live": "polite", children: [_jsx(Loader2, { className: "size-4 animate-spin" }), _jsx("span", { children: t({ en: 'Loading locations…', de: 'Lade Standorte…', it: 'Caricamento località…' }) })] })), status === 'error' && (_jsx("p", { className: "pt-3 text-sm text-destructive", role: "status", "aria-live": "polite", children: error })), status === 'results' && (_jsx("ul", { className: "mt-2 flex flex-col gap-1 pb-3", children: suggestions.map((suggestion, index) => (_jsx("li", { children: _jsxs("button", { type: "button", className: cn('flex w-full items-center justify-between gap-2 rounded-2xl px-3 py-2 text-left text-sm transition-colors', index === activeIndex
                                            ? 'bg-accent text-accent-foreground'
                                            : 'hover:bg-accent/60'), onMouseEnter: () => setActiveIndex(index), onClick: () => handlePickSuggestion(suggestion), children: [_jsx("span", { className: "truncate", children: suggestion.label }), _jsx("span", { className: "shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground", children: suggestion.locationType })] }) }, suggestion.id))) })), status !== 'results' && status !== 'loading' && (_jsxs("div", { className: "pt-4", children: [_jsx("p", { className: "pb-2 text-xs font-medium tracking-wide text-muted-foreground", children: fallbackTitle }), _jsx("ul", { className: "flex flex-col gap-1 pb-3", children: fallbackLocations.map((location) => (_jsx("li", { children: _jsxs("button", { type: "button", className: "flex w-full items-center justify-between gap-2 py-2 text-left text-sm transition-colors", onClick: () => handlePickSuggestion(location), children: [_jsx("span", { className: "truncate", children: location.label }), _jsx("span", { className: "shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground", children: location.locationType })] }) }, location.id))) })] }))] })] }) }) }));
}
